import type { PlaceLocation } from '@/components/ui/google-places/google-places-autocomplete';
import { z } from 'zod/v4';

const requiredAddressSchema = z.object({
  streetAddress: z.string().trim().min(1),
  city: z.string().trim().min(1),
  state: z.string().trim().optional(),
  postalCode: z.string().trim().min(1),
  country: z.string().trim().min(1),
});

export type AddressValidationField = 'streetAddress' | 'city' | 'state' | 'postalCode' | 'country';

export interface AddressValidationInput {
  required: boolean;
  streetAddress: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
  stateRequired?: boolean;
}

export interface ParsedPlaceAddress {
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export function getMissingAddressFields(input: AddressValidationInput): AddressValidationField[] {
  if (!input.required) {
    return [];
  }

  const validation = requiredAddressSchema.safeParse({
    streetAddress: input.streetAddress,
    city: input.city,
    state: input.state,
    postalCode: input.postalCode,
    country: input.country,
  });

  const missingFields = new Set<AddressValidationField>();

  if (!validation.success) {
    for (const issue of validation.error.issues) {
      const field = issue.path[0];
      if (
        field === 'streetAddress' ||
        field === 'city' ||
        field === 'state' ||
        field === 'postalCode' ||
        field === 'country'
      ) {
        missingFields.add(field);
      }
    }
  }

  if (input.stateRequired && !(input.state ?? '').trim()) {
    missingFields.add('state');
  }

  return Array.from(missingFields);
}

export function parseGooglePlaceAddress(
  place: PlaceLocation,
  existingAddress: ParsedPlaceAddress,
): ParsedPlaceAddress {
  const components = place.addressComponents ?? [];
  const get = (type: string, useShort = false) =>
    components.find((component) => component.types.includes(type))?.[
      useShort ? 'shortName' : 'longName'
    ] ?? '';

  const streetNumber = get('street_number');
  const route = get('route');
  const subpremise = get('subpremise');
  const streetAddress =
    [streetNumber, route].filter(Boolean).join(' ') || existingAddress.streetAddress;
  const addressLine2 = existingAddress.addressLine2 || subpremise || '';
  const city =
    get('locality') ||
    get('postal_town') ||
    get('administrative_area_level_2') ||
    existingAddress.city;
  const state = get('administrative_area_level_1') || existingAddress.state;
  const postalCode = get('postal_code') || existingAddress.postalCode;
  const country = get('country', true) || existingAddress.country;

  return {
    streetAddress,
    addressLine2,
    city,
    state,
    postalCode,
    country,
  };
}
